import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  computed,
  forwardRef,
  signal,
} from '@angular/core';
import {
  CdkConnectedOverlay,
  ConnectedPosition,
  OverlayModule,
  ScrollStrategy,
  createCloseScrollStrategy,
} from '@angular/cdk/overlay';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

let idCounter = 0;

/**
 * Unter dem Feld, bei zu wenig Platz darueber. Das Umschlagen uebernimmt die
 * Positionsstrategie des Overlays.
 */
const PANEL_POSITIONS: ConnectedPosition[] = [
  {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4},
  {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4},
];

@Component({
  selector: 'app-ci-multiselect',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule],
  templateUrl: './ci-multiselect.component.html',
  styles: [':host { display: block; width: 100%; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CiMultiselectComponent),
      multi: true,
    },
  ],
})
export class CiMultiselectComponent<T>
  implements ControlValueAccessor, OnDestroy
{
  private readonly _items = signal<readonly T[]>([]);

  @Input({ required: true })
  set items(value: readonly T[]) {
    this._items.set(value ?? []);
  }
  get items(): readonly T[] {
    return this._items();
  }

  @Input() variant: 'outline' | 'filled' = 'outline';
  @Input() label = '';
  @Input() required = false;
  @Input() summary = false;
  @Input() summaryTemplate = '';
  @Input() placeholder = '';
  @Input() searchPlaceholder = '';
  @Input() ariaLabel = '';
  @Input() clearLabel = '';
  @Input() countTemplate = '';
  @Input() emptyLabel = '';
  @Input() errorText = '';
  /** `{item}` wird durch die Beschriftung des Chips ersetzt. */
  @Input() removeChipTemplate = '';
  @Input() displayWith: (item: T) => string = (item) => String(item ?? '');
  @Input() compareWith: (a: T, b: T) => boolean = (a, b) => a === b;
  @Input() searchFn: (item: T, term: string) => boolean = (item, term) =>
    this.displayWith(item).toLowerCase().includes(term);
  @Input() isDisabled: (item: T) => boolean = () => false;

  @Input()
  set invalid(value: boolean) {
    this._invalid.set(!!value);
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled.set(!!value);
  }

  @Output() selectionChange = new EventEmitter<T[]>();
  @Output() openChange = new EventEmitter<boolean>();

  @ViewChild('field') fieldRef?: ElementRef<HTMLElement>;
  @ViewChild('search') searchRef?: ElementRef<HTMLInputElement>;
  @ViewChild(CdkConnectedOverlay) private overlay?: CdkConnectedOverlay;

  protected readonly id = `ci-multiselect-${++idCounter}`;
  protected readonly errorId = `${this.id}-error`;

  protected readonly open = signal(false);
  protected readonly panelWidth = signal(0);
  protected readonly panelPositions = PANEL_POSITIONS;
  // Schliessen statt Nachfuehren: beim Nachfuehren stauchte ein nach oben geoeffnetes Panel
  // und legte sich ueber das Feld. Ohne Optionen — ein threshold > 1 nimmt in
  // CloseScrollStrategy den Zweig, der nachfuehrt statt zu schliessen.
  protected readonly scrollStrategy: ScrollStrategy;
  protected readonly searchTerm = signal('');
  protected readonly focusedIndex = signal<number>(-1);
  protected readonly value = signal<T[]>([]);
  protected readonly _invalid = signal(false);
  protected readonly _disabled = signal(false);

  protected readonly filtered = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const items = this._items();
    if (!term) return [...items];
    return items.filter((i) => this.searchFn(i, term));
  });

  // Methoden statt computed: die Vorlagen kommen als uebersetzte Strings herein
  // und wechseln mit der Sprache, ein Memo darauf bliebe auf der alten stehen.
  protected countText(): string {
    return this.fill(this.countTemplate);
  }

  protected summaryText(): string {
    return this.fill(this.summaryTemplate);
  }

  protected removeChipLabel(item: T): string {
    return this.removeChipTemplate
      ? this.removeChipTemplate.replace('{item}', this.displayWith(item))
      : this.displayWith(item);
  }

  private fill(template: string): string {
    return template
      .replace('{selected}', String(this.value().length))
      .replace('{total}', String(this._items().length));
  }

  protected optionId(index: number): string {
    return `${this.id}-opt-${index}`;
  }

  protected isSelected(item: T): boolean {
    return this.value().some((v) => this.compareWith(v, item));
  }

  protected onFieldClick(event?: MouseEvent): void {
    if (this._disabled()) return;
    // Der Klick in das Suchfeld des Chip-Modus darf das Panel nicht zuklappen.
    const target = event?.target as HTMLElement | null;
    if (this.open() && target?.closest('input')) return;
    if (this.open()) {
      this.setOpen(false);
      this.onTouched();
      return;
    }
    this.openPanel();
  }

  private openPanel(): void {
    this.syncPanelWidth();
    this.setOpen(true);
  }

  private syncPanelWidth(): void {
    this.panelWidth.set(this.fieldRef?.nativeElement.offsetWidth ?? 0);
  }

  /**
   * Das Panel haengt am Feld, nicht im Seitenfluss. Brechen die Chips in eine zweite Zeile um,
   * waechst das Feld — ohne Nachziehen legte sich das Panel darueber. Die Positionsstrategie
   * tut das von sich aus nur bei Viewport-Aenderung.
   */
  protected onFieldGeometryChange(): void {
    this.syncPanelWidth();
    const ref = this.overlay?.overlayRef;
    if (ref?.hasAttached()) ref.updatePosition();
  }

  /** Nur solange offen, damit nicht jedes Feld der Seite an jedem Ereignis haengt. */
  private trackWhileOpen(track: boolean): void {
    if (track === !!this.stopTracking) return;
    if (!track) {
      this.stopTracking?.();
      this.stopTracking = undefined;
      return;
    }
    const cleanups: (() => void)[] = [];

    const field = this.fieldRef?.nativeElement;
    if (field && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.onFieldGeometryChange());
      observer.observe(field);
      cleanups.push(() => observer.disconnect());
    }

    // overlayOutsideClick haengt an `click`. Ein Druck auf die Scrollbar erzeugt keinen —
    // vor der Overlay-Umstellung schloss das Panel per mousedown, das bleibt so.
    const onMouseDown = (event: MouseEvent) => this.onDocumentMouseDown(event);
    document.addEventListener('mousedown', onMouseDown);
    cleanups.push(() => document.removeEventListener('mousedown', onMouseDown));

    this.stopTracking = () => cleanups.forEach((fn) => fn());
  }

  private onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) return;
    if (this.host.nativeElement.contains(target)) return;
    if (this.overlayElement()?.contains(target)) return;
    this.setOpen(false);
    this.onTouched();
  }

  private overlayElement(): HTMLElement | null {
    return this.overlay?.overlayRef?.overlayElement ?? null;
  }

  /** Im Summary-Modus liegt das Suchfeld im Overlay, sonst im Feld. */
  private panelInput(): HTMLInputElement | null {
    return this.overlayElement()?.querySelector<HTMLInputElement>(
      '.ci-multiselect__panel-search',
    ) ?? null;
  }

  protected onOverlayAttached(): void {
    if (this.summary) this.panelInput()?.focus();
    else this.searchRef?.nativeElement.focus();
  }

  /**
   * Ein Detach ohne vorheriges Schliessen kommt von der Scroll-Strategie, nicht vom Nutzer —
   * darum der emissionsfreie Pfad und kein `onTouched()`.
   */
  protected onDetach(): void {
    this.applyOpen(false);
  }

  protected onOutsideClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) return;
    this.setOpen(false);
    this.onTouched();
  }

  /**
   * Der Fokus steht beim Schliessen im Suchfeld des Panels, das gleich aus dem DOM
   * fliegt. Ohne Rueckgabe an das Feld landet er auf `body` und Tab beginnt vorn.
   */
  private focusField(): void {
    queueMicrotask(() => {
      const field = this.fieldRef?.nativeElement;
      if (this.summary) field?.focus();
      else this.searchRef?.nativeElement.focus();
    });
  }

  protected onSearchInput(term: string): void {
    this.searchTerm.set(term ?? '');
    this.focusedIndex.set(this.filtered().length > 0 ? 0 : -1);
    // openPanel(), nicht setOpen(true): sonst oeffnet das Panel mit Breite 0.
    if (!this.open()) this.openPanel();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this._disabled()) return;
    const list = this.filtered();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.open()) {
          this.openPanel();
          return;
        }
        this.focusedIndex.update((i) => (list.length ? (i + 1) % list.length : -1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.open()) {
          this.openPanel();
          return;
        }
        this.focusedIndex.update((i) =>
          list.length ? (i - 1 + list.length) % list.length : -1,
        );
        break;
      case 'Enter':
        if (!this.open()) {
          event.preventDefault();
          this.openPanel();
          break;
        }
        if (this.focusedIndex() >= 0) {
          event.preventDefault();
          const item = list[this.focusedIndex()];
          if (item !== undefined) this.toggle(item);
        }
        break;
      // Oeffnet nur das geschlossene Feld. In einem Suchfeld gehoert das Leerzeichen
      // in den Text — sonst ist kein Begriff mit Leerzeichen tippbar (Review #1).
      case ' ':
        if (!this.open() && !(event.target as HTMLElement | null)?.closest('input')) {
          event.preventDefault();
          this.openPanel();
        }
        break;
      // Nur im Chip-Modus: im Summary-Modus ist die Auswahl nicht sichtbar, das
      // Leeren der Suche wuerde dort unbemerkt den letzten Eintrag loeschen.
      case 'Backspace':
        if (!this.summary && !this.searchTerm() && this.value().length > 0) {
          event.preventDefault();
          this.removeAt(this.value().length - 1);
        }
        break;
      case 'Escape':
        if (this.open()) {
          event.preventDefault();
          this.setOpen(false);
          this.focusField();
        }
        this.onTouched();
        break;
      case 'Tab':
        // Das fokussierte Suchfeld liegt im Overlay und fliegt gleich aus dem DOM; ohne
        // Rueckgabe landet der Fokus auf `body` und Tab beginnt vorn.
        if (this.open()) this.focusField();
        this.setOpen(false);
        this.onTouched();
        break;
    }
  }

  protected toggle(item: T): void {
    if (this._disabled() || this.isDisabled(item)) return;
    const current = this.value();
    const exists = current.some((v) => this.compareWith(v, item));
    const next = exists
      ? current.filter((v) => !this.compareWith(v, item))
      : [...current, item];
    this.applyValue(next);
  }

  protected removeAt(index: number, event?: Event): void {
    event?.stopPropagation();
    if (this._disabled()) return;
    const next = this.value().filter((_, i) => i !== index);
    this.applyValue(next);
  }

  protected clearAll(event: Event): void {
    event.stopPropagation();
    if (this._disabled()) return;
    this.applyValue([]);
  }

  private applyValue(next: T[]): void {
    this.value.set(next);
    this.onChange(next);
    this.onTouched();
    this.selectionChange.emit(next);
  }

  /**
   * Einziger Schreibpfad auf `open` mit Emission. `openChange` faellt hier statt in einem Effect:
   * eine Emission mitten in der Change Detection laesst den Handler des Aufrufers
   * (Zertifikatsfilter: `search()`) waehrend des Zyklus schreiben — NG0100 im
   * Dev-Modus, ein zusaetzlicher Durchlauf in Prod.
   */
  private setOpen(next: boolean): void {
    if (this.open() === next) return;
    this.applyOpen(next);
    this.openChange.emit(next);
  }

  private applyOpen(next: boolean): void {
    this.open.set(next);
    this.trackWhileOpen(next);
    if (!next) {
      this.searchTerm.set('');
      this.focusedIndex.set(-1);
    }
  }

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    injector: Injector,
  ) {
    this.scrollStrategy = createCloseScrollStrategy(injector);
  }

  /**
   * Ohne Emission, und das ist keine Wahl: die Zerstoerung faellt mitten in die Change
   * Detection des Aufrufers, synchron waere sie NG0100 — und aufgeschoben hoert sie
   * niemand mehr, weil die Ausgabe-Bindung mit der Ansicht abgeraeumt wird. Das Overlay
   * selbst entsorgt CdkConnectedOverlay.ngOnDestroy mit der Komponente.
   */
  ngOnDestroy(): void {
    this.applyOpen(false);
  }

  writeValue(value: T[] | null): void {
    this.value.set(Array.isArray(value) ? [...value] : []);
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled.set(isDisabled);
  }

  private stopTracking?: () => void;
  private onChange: (value: T[]) => void = () => {};
  private onTouched: () => void = () => {};
}